import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronUp } from "lucide-react";
import { panelTokens } from "../config/graphThemes";

const MANUAL_COLOR_DARK = "rgba(255,255,255,0.90)";
const MANUAL_COLOR_LIGHT = "rgba(100,116,139,0.85)";
const MANUAL_COLOR_MUTED_DARK = "rgba(148,163,184,0.30)";
const MANUAL_COLOR_MUTED_LIGHT = "rgba(71,85,105,0.35)";

const StatRow = ({ label, total, gen, manual, dark, tk }) => {
  const genPct = total > 0 ? (gen / total) * 100 : 0;
  const manualActive = manual > 0;
  const manualColor = manualActive
    ? dark
      ? MANUAL_COLOR_DARK
      : MANUAL_COLOR_LIGHT
    : dark
      ? MANUAL_COLOR_MUTED_DARK
      : MANUAL_COLOR_MUTED_LIGHT;

  return (
    <div className="py-2">
      <div className="flex items-baseline justify-between mb-1.5">
        <span
          className="text-[11px] uppercase tracking-[0.07em] font-semibold"
          style={{ color: tk.mutedText }}
        >
          {label}
        </span>
        <span
          className="text-lg font-medium leading-none tabular-nums"
          style={{
            color: tk.cyanText,
            textShadow: dark ? `0 0 12px rgba(${tk.cyanRgb}, 0.45)` : "none",
          }}
        >
          {total}
        </span>
      </div>

      <div
        className="h-[3px] rounded-full mb-1.5 overflow-hidden"
        style={{
          background: dark ? "rgba(148,163,184,0.10)" : "rgba(71,85,105,0.12)",
        }}
      >
        <div
          className="h-full rounded-full transition-[width] duration-[400ms] ease-in-out"
          style={{
            width: `${genPct}%`,
            background: `rgba(${tk.cyanRgb}, ${dark ? "0.45" : "0.55"})`,
          }}
        />
      </div>

      <div className="flex justify-between">
        <span
          className="text-[11px]"
          style={{
            color: dark ? "rgba(148,163,184,0.45)" : "rgba(71,85,105,0.55)",
          }}
        >
          <span
            className="font-semibold tabular-nums"
            style={{
              color: dark ? "rgba(148,163,184,0.70)" : "rgba(51,65,85,0.80)",
            }}
          >
            {gen}
          </span>{" "}
          generated
        </span>
        <span
          className="text-[11px]"
          style={{
            color: dark ? "rgba(148,163,184,0.45)" : "rgba(71,85,105,0.55)",
          }}
        >
          <span
            className="font-semibold tabular-nums"
            style={{
              color: manualColor,
              textShadow:
                manualActive && dark
                  ? `0 0 6px rgba(${tk.cyanRgb},0.35)`
                  : "none",
            }}
          >
            {manual}
          </span>{" "}
          manual
        </span>
      </div>
    </div>
  );
};

const StatsPanel = ({
  dark,
  t,
  graphStats,
  backendTotals,
  tooltipNode,
  isDraggingFromPlus,
  truncate,
  formatType,
}) => {
  const tk = panelTokens(dark);
  const [open, setOpen] = useState(true);

  const genNodes = graphStats.nodes - graphStats.manualNodes;
  const genEdges = graphStats.edges - graphStats.manualEdges;

  const hasBackendTotals =
    backendTotals && (backendTotals.nodes > 0 || backendTotals.edges > 0);

  const panelBg =
    tk.innerGlow !== "none"
      ? `${tk.innerGlow}, ${t.surface.bgSolid}`
      : t.surface.bgSolid;

  const orphanActive = graphStats.orphanCount > 0;
  const orphanColor = orphanActive
    ? dark
      ? MANUAL_COLOR_DARK
      : MANUAL_COLOR_LIGHT
    : dark
      ? MANUAL_COLOR_MUTED_DARK
      : MANUAL_COLOR_MUTED_LIGHT;

  return (
    <motion.div
      className="absolute z-20 left-3 top-14 flex flex-col gap-2 pointer-events-none"
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -12 }}
      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div
        className="pointer-events-auto relative rounded-lg backdrop-blur-md overflow-hidden w-60"
        style={{
          background: panelBg,
          border: `1px solid ${tk.border}`,
          animation: "arwes-panel-pulse 5s ease-in-out infinite",
        }}
      >
        {/* Header */}
        <button
          className="relative z-[1] w-full flex items-center justify-between px-3 py-2 cursor-pointer"
          style={{ borderBottom: open ? `1px solid ${tk.divider}` : "none" }}
          onClick={() => setOpen(!open)}
        >
          <span
            className="text-[11px] uppercase tracking-[0.08em] font-semibold"
            style={{ color: tk.mutedText }}
          >
            Stats
          </span>
          <ChevronUp
            size={13}
            style={{
              color: tk.mutedText,
              transform: open ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.2s ease",
            }}
          />
        </button>

        {open && (
          <div className="relative z-[1] flex flex-col px-3">
            <StatRow
              label="Nodes"
              total={graphStats.nodes}
              gen={genNodes}
              manual={graphStats.manualNodes}
              dark={dark}
              tk={tk}
            />

            <div className="h-px" style={{ background: tk.divider }} />

            <StatRow
              label="Edges"
              total={graphStats.edges}
              gen={genEdges}
              manual={graphStats.manualEdges}
              dark={dark}
              tk={tk}
            />

            <div className="h-px" style={{ background: tk.divider }} />

            {/* Orphans */}
            <div className="flex items-center justify-between py-1.5">
              <span
                className="text-[11px] uppercase tracking-[0.07em] font-semibold"
                style={{ color: tk.mutedText }}
              >
                Orphans
              </span>
              <span
                className="text-[15px] font-bold tabular-nums"
                style={{
                  color: orphanColor,
                  textShadow:
                    orphanActive && dark
                      ? "0 0 10px rgba(251,191,36,0.35)"
                      : "none",
                }}
              >
                {graphStats.orphanCount}
              </span>
            </div>

            {/* Available footer */}
            {hasBackendTotals && (
              <div
                className="flex items-center justify-between py-1.5"
                style={{ borderTop: `1px solid ${tk.divider}` }}
              >
                <span
                  className="text-[10px] uppercase tracking-[0.06em]"
                  style={{
                    color: dark
                      ? "rgba(148,163,184,0.35)"
                      : "rgba(71,85,105,0.40)",
                  }}
                >
                  Available
                </span>
                <span
                  className="text-[11px] tabular-nums"
                  style={{
                    color: dark
                      ? "rgba(148,163,184,0.45)"
                      : "rgba(71,85,105,0.55)",
                  }}
                >
                  {backendTotals.nodes.toLocaleString()} nodes ·{" "}
                  {backendTotals.edges.toLocaleString()} edges
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default StatsPanel;
